import { useState } from 'react';
import type { JobInput, JobStatus } from '../../api/client';

const STATUSES: JobStatus[] = ['DRAFT', 'OPEN', 'CLOSED'];

interface Props {
  initial?: Partial<JobInput>;
  submitLabel: string;
  pending: boolean;
  error?: string | null;
  onSubmit: (values: JobInput) => void;
  onCancel?: () => void;
}

export default function JobForm({ initial, submitLabel, pending, error, onSubmit, onCancel }: Props) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [department, setDepartment] = useState(initial?.department ?? '');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [status, setStatus] = useState<JobStatus>(initial?.status ?? 'DRAFT');
  const [thresholdPct, setThresholdPct] = useState(
    Math.round((initial?.reviewThreshold ?? 0.5) * 100),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = () => {
    setLocalError(null);
    if (!title.trim()) return setLocalError('Title is required');
    if (!description.trim()) return setLocalError('Description is required');
    if (thresholdPct < 0 || thresholdPct > 100) return setLocalError('Threshold must be 0–100%');
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      department: department.trim() || null,
      location: location.trim() || null,
      status,
      reviewThreshold: thresholdPct / 100,
    });
  };

  return (
    <div className="form">
      {(localError || error) && <div className="banner-error">{localError || error}</div>}

      <label className="field">
        <span>Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Senior Backend Engineer" />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea
          rows={6}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Responsibilities, requirements, etc."
        />
      </label>

      <div className="field-row">
        <label className="field">
          <span>Department</span>
          <input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Engineering" />
        </label>
        <label className="field">
          <span>Location</span>
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Remote / Berlin" />
        </label>
      </div>

      <div className="field-row">
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as JobStatus)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Approval threshold (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            value={thresholdPct}
            onChange={(e) => setThresholdPct(Number(e.target.value))}
          />
          <small className="muted">Share of assigned reviewers who must approve before HR can advance.</small>
        </label>
      </div>

      <div className="form-actions">
        <button className="btn-primary" onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button className="btn-ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
